<template>
  <footer class="footer-modern">
    <!-- Main Footer Content -->
    <div class="footer-main">
      <div class="container mx-auto px-6 py-12">
        <div class="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-12">
          
          <!-- Brand Section -->
          <div class="footer-brand lg:col-span-1">
            <img :src="logoPath" alt="Insurance Express" class="footer-brand-logo" />
            <p class="footer-brand-description">
              Your trusted insurance partner providing comprehensive coverage and exceptional service since 1999.
            </p>
            <!-- Trust Badges -->
            <div class="mb-6 space-y-2">
              <div class="trust-badge">
                <Icon icon="mdi:shield-check" width="16" height="16" />
                <span>Licensed & Bonded</span>
              </div>
              <div class="trust-badge">
                <Icon icon="mdi:security" width="16" height="16" />
                <span>Secure & Protected</span>
              </div>
              <div class="trust-badge">
                <Icon icon="mdi:office-building-cog" width="16" height="16" />
                <span>Multiple Carrier Options</span>
              </div>
              <div class="trust-badge">
                <Icon icon="mdi:map-outline" width="16" height="16" />
                <span>Nationwide Coverage</span>
              </div>
            </div>
          </div>

          <!-- Insurance Products -->
          <div class="footer-section">
            <h3 class="footer-heading">Insurance Products</h3>
            <ul class="footer-links">
              <li><a href="/products/autoinsurance" class="footer-link">Auto Insurance</a></li>
              <li><a href="/products/homeinsurance" class="footer-link">Home Insurance</a></li>
              <li><a href="/products/lifeinsurance" class="footer-link">Life Insurance</a></li>
              <li><a href="/products/businessinsurance" class="footer-link">Business Insurance</a></li>
              <li><a href="/products/floodinsurance" class="footer-link">Flood Insurance</a></li>
              <li><a href="/products/umbrellainsurance" class="footer-link">Umbrella Insurance</a></li>
              <li><a href="/products/medicare" class="footer-link">Medicare</a></li>
            </ul>
          </div>

          <!-- Customer Support -->
          <div class="footer-section">
            <h3 class="footer-heading">Customer Support</h3>
            <ul class="footer-links">
              <li><a href="/customerservice/carrier-directory/make-claim" class="footer-link">File a Claim</a></li>
              <li><a href="/customerservice/carrier-directory/make-payment" class="footer-link">Make a Payment</a></li>
              <li><a href="/policy-search" class="footer-link">Policy Search</a></li>
              <li><a href="/customerservice" class="footer-link">Customer Service</a></li>
              <li><a href="/aboutus" class="footer-link">About Us</a></li>
              <li><a @click="showMemberCompanies = true" class="footer-link cursor-pointer">Member Companies</a></li>
            </ul>
          </div>

          <!-- Contact Information -->
          <div class="footer-section">
            <h3 class="footer-heading">Get In Touch</h3>
            <div class="space-y-4">
              <div class="contact-item">
                <Icon icon="mdi:phone" class="contact-icon" />
                <div>
                  <a href="tel:800-268-9137" class="footer-link text-lg font-semibold">800-268-9137</a>
                  <p class="text-gray-600 text-xs mt-1">Mon-Fri 8AM-6PM EST</p>
                </div>
              </div>
              <div class="contact-item">
                <Icon icon="mdi:email" class="contact-icon" />
                <div>
                  <a href="mailto:customerservice@insuranceexpress.com" class="footer-link break-all">customerservice@insuranceexpress.com</a>
                </div>
              </div>
              <div class="contact-item">
                <Icon icon="mdi:map-marker" class="contact-icon" />
                <div>
                  <a href="https://maps.google.com/?q=2005+Vista+Parkway+Suite+200,+West+Palm+Beach,+FL+33411" target="_blank" rel="noopener noreferrer" class="footer-link">
                    2005 Vista Parkway Suite 200<br>West Palm Beach, FL 33411
                  </a>
                </div>
              </div>
            </div>
            
            <!-- Quick Quote CTA -->
            <div class="mt-6">
              <a href="/chooseproducts" class="cta-button">
                <Icon icon="mdi:calculator" class="mr-2" width="18" height="18" />
                Get Quick Quote
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom Bar -->
    <div class="footer-bottom">
      <div class="container mx-auto px-6 py-6">
        <div class="flex flex-col md:flex-row md:justify-between md:items-center space-y-4 md:space-y-0">
          <p class="text-white text-sm">
            © {{ new Date().getFullYear() }} Insurance Express and Member Companies. All rights reserved.
          </p>
          <div class="flex flex-wrap gap-6 text-sm">
            <a @click="showPrivacyPolicy = true" class="footer-bottom-link">Privacy Policy</a>
            <a @click="showTermsOfService = true" class="footer-bottom-link">Terms of Service</a>
            <a href="/sitemap" class="footer-bottom-link">Sitemap</a>
            <a href="https://www.indeed.com/cmp/Insurance-Express-&-Member-Companies" target="_blank" rel="noopener noreferrer" class="footer-bottom-link">Careers</a>
          </div>
        </div>
      </div>
    </div>

    <!-- Member Companies Modal -->
    <transition name="modal">
      <div v-if="showMemberCompanies" class="modal-overlay" @click="showMemberCompanies = false">
        <div class="modal-content" @click.stop>
          <button class="modal-close" @click="showMemberCompanies = false">✕</button>
          <h2 class="text-2xl font-bold mb-6">Our Member Companies</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <a v-for="company in memberCompanies" :key="company.name" 
               :href="company.link" 
               target="_blank" 
               rel="noopener noreferrer"
               class="member-company-card">
              <h3 class="font-semibold text-gray-900 mb-1">{{ company.name }}</h3>
              <p class="text-sm text-gray-600">{{ company.description }}</p>
              <Icon icon="mdi:external-link" class="text-blue-500 mt-2" width="16" height="16" />
            </a>
          </div>
        </div>
      </div>
    </transition>

    <!-- Privacy Policy Modal -->
    <transition name="modal">
      <div v-if="showPrivacyPolicy" class="modal-overlay" @click="showPrivacyPolicy = false">
        <div class="modal-content" @click.stop>
          <button class="modal-close" @click="showPrivacyPolicy = false">✕</button>
          <h2 class="text-2xl font-bold mb-4">Privacy Policy</h2>
          <div class="privacy-content space-y-4 text-sm text-gray-700 max-h-96 overflow-y-auto">
            <p>Last updated: 10/17/2024</p>
            <hr class="my-4" />
            <h3 class="text-lg font-semibold">Introduction</h3>
            <p>
              At Insurance Express.com (GLV Insurance Agency Inc DBA Insurance Express.com), we are committed to protecting your personal information and respecting your privacy. This Privacy Policy outlines how we collect, use, disclose, and safeguard your information when you visit our website <a href="https://www.insuranceexpress.com" class="text-blue-500 underline">https://www.insuranceexpress.com</a> or communicate with us, including through SMS/MMS text messaging.
            </p>
            <h3 class="text-lg font-semibold">Information We Collect</h3>
            <ul class="list-disc pl-5">
              <li><strong>Personal Information</strong>
                <p>We may collect the following personal information when you submit service requests, request insurance quotes, or opt-in to receive SMS/MMS text messages:</p>
                <ul class="list-disc pl-5">
                  <li><strong>Contact Information:</strong> Name, email address, phone number, and physical address.</li>
                  <li><strong>Non-Personal Data:</strong> IP addresses, browser types, device information, and browsing actions collected through cookies and similar technologies.</li>
                </ul>
                <p><em>Note: We do not collect sensitive personal information such as date of birth, Social Security numbers, driver's license numbers, or health information.</em></p>
              </li>
            </ul>
            <h3 class="text-lg font-semibold">Use of Your Information</h3>
            <p>We use the collected information to:</p>
            <ul class="list-disc pl-5">
              <li><strong>Provide Services:</strong> Process your service requests and provide insurance quotes.</li>
              <li><strong>Marketing and Analytics:</strong> Conduct marketing activities, send promotional materials, and perform analytics to improve our services.</li>
              <li><strong>Third-Party Services:</strong> Share information with third parties as needed for marketing, analytics, and service provision.</li>
              <li><strong>Communication:</strong> Respond to your inquiries and send administrative information.</li>
            </ul>
            <h3 class="text-lg font-semibold">SMS/MMS Text Messaging</h3>
            <p>By providing your mobile number and opting in, you consent to receive SMS/MMS text messages from us regarding our services, including but not limited to policy information, reminders, and promotional offers.</p>
            <ul class="list-disc pl-5">
              <li><strong>Opt-Out:</strong> You may opt-out of receiving text messages at any time by following the instructions provided in the message or by contacting us directly.</li>
              <li><strong>Message and Data Rates:</strong> Standard message and data rates may apply according to your mobile plan.</li>
            </ul>
            <h3 class="text-lg font-semibold">Data Sharing and Disclosure</h3>
            <ul class="list-disc pl-5">
              <li><strong>Third-Party Partners:</strong> We may share your information with insurance carriers, underwriters, marketing partners, and other third-party service providers. This is done to facilitate our services and may occur without obtaining additional consent at the time of sharing.</li>
              <li><strong>SMS/MMS Consent Data:</strong>
                <ul class="list-disc pl-5">
                  <li><em>Exclusion of SMS Consent Data:</em> All the above categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties except as necessary to provide you with the messaging services.</li>
                  <li><em>Limited Sharing for Messaging Services:</em> We will not share your opt-in to an SMS campaign with any third party for purposes unrelated to providing you with the services of that campaign. We may share your personal data, including your SMS opt-in or consent status, with third parties that help us provide our messaging services, including but not limited to platform providers, phone companies, and any other vendors who assist us in the delivery of text messages.</li>
                </ul>
              </li>
              <li><strong>Legal Obligations:</strong> We may disclose your information to comply with legal obligations, regulations, or valid governmental requests.</li>
            </ul>
            <h3 class="text-lg font-semibold">Cookies and Tracking Technologies</h3>
            <ul class="list-disc pl-5">
              <li><strong>Future Use of Cookies:</strong> While we do not currently use cookies or similar tracking technologies, we reserve the right to implement them in the future to enhance user experience and analyze website performance.</li>
              <li><strong>Notification of Changes:</strong> If we begin using cookies or tracking technologies, we will update this Privacy Policy accordingly and provide appropriate notices.</li>
            </ul>
            <h3 class="text-lg font-semibold">Data Security</h3>
            <p>We implement reasonable security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction. Our security measures include:</p>
            <ul class="list-disc pl-5">
              <li><strong>SSL Encryption:</strong> All data transmitted between your browser and our website is encrypted using Secure Sockets Layer (SSL) technology.</li>
              <li><strong>Secure Servers:</strong> We host our website on secure AWS (Amazon Web Services) servers with standard security protocols.</li>
              <li><strong>PCI DSS Compliance:</strong> If we process payment transactions in the future, we will ensure compliance with the Payment Card Industry Data Security Standard (PCI DSS) to protect your payment information.</li>
            </ul>
            <h3 class="text-lg font-semibold">Data Retention</h3>
            <p>We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy. We ensure data is stored securely throughout its lifecycle and will delete or anonymize data when it is no longer needed.</p>
            <h3 class="text-lg font-semibold">Your Rights and Choices</h3>
            <p>Under applicable data protection laws, including the General Data Protection Regulation (GDPR), you may have the following rights:</p>
            <ul class="list-disc pl-5">
              <li><strong>Access:</strong> You have the right to request access to the personal information we hold about you.</li>
              <li><strong>Rectification:</strong> You may request that we correct any inaccurate or incomplete personal information.</li>
              <li><strong>Erasure ("Right to be Forgotten"):</strong> You have the right to request the deletion of your personal information under certain circumstances.</li>
              <li><strong>Restriction of Processing:</strong> You may request that we restrict the processing of your personal information under certain conditions.</li>
              <li><strong>Data Portability:</strong> You have the right to receive your personal information in a structured, commonly used, and machine-readable format.</li>
              <li><strong>Objection:</strong> You may object to the processing of your personal information for direct marketing purposes or on grounds relating to your particular situation.</li>
            </ul>
            <p>To exercise any of these rights, please contact us using the contact information provided below.</p>
            <ul class="list-disc pl-5">
              <li><strong>Marketing Communications:</strong> You may opt-out of receiving marketing emails by clicking the "unsubscribe" link provided in the emails or by contacting us directly.</li>
              <li><strong>Access and Correction:</strong> While we do not offer an online portal for users to access or update their personal information, you can contact us to request changes or deletion of your information.</li>
            </ul>
            <h3 class="text-lg font-semibold">International Users</h3>
            <p>Our services are intended for users within the United States. However, if you are accessing our services from the European Union or other regions with laws governing data collection and use, please note that you are transferring your personal data to the United States, which may not have the same data protection laws as your jurisdiction.</p>
            <h3 class="text-lg font-semibold">Accessibility</h3>
            <p>We are committed to ensuring that our website is accessible to individuals with disabilities, in compliance with the Americans with Disabilities Act (ADA). If you experience any difficulty accessing any part of our website, please contact us, and we will work with you to provide the information or service you need.</p>
            <h3 class="text-lg font-semibold">Changes to This Privacy Policy</h3>
            <p>We may update this Privacy Policy from time to time. All changes will be effective immediately upon posting the updated policy on this page. We encourage you to review this Privacy Policy periodically for any changes.</p>
            <h3 class="text-lg font-semibold">Contact Us</h3>
            <p>If you have any questions or concerns about this Privacy Policy, or if you wish to exercise your data protection rights, please contact us:</p>
            <p>
              GLV Insurance Agency Inc DBA Insurance Express.com<br />
              2005 Vista Parkway Suite 200<br />
              West Palm Beach, FL 33411<br />
              Phone: <a href="tel:800-268-9137" class="text-blue-500 underline">800-268-9137</a><br />
              Email: <a href="mailto:customerservice@insuranceexpress.com" class="text-blue-500 underline">customerservice@insuranceexpress.com</a>
            </p>
            <hr class="my-4" />
            <div class="text-center mt-6 pt-4 border-t">
              <p class="text-xs text-gray-500">© 2024 Insurance Express and Member Companies</p>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Terms of Service Modal -->
    <transition name="modal">
      <div v-if="showTermsOfService" class="modal-overlay" @click="showTermsOfService = false">
        <div class="modal-content" @click.stop>
          <button class="modal-close" @click="showTermsOfService = false">✕</button>
          <h2 class="text-2xl font-bold mb-4">Terms of Service</h2>
          <div class="privacy-content space-y-4 text-sm text-gray-700 max-h-96 overflow-y-auto">
            <p>Last updated: {{ new Date().toLocaleDateString() }}</p>
            <hr class="my-4" />
            <h3 class="text-lg font-semibold">Agreement to Terms</h3>
            <p>
              By accessing and using Insurance Express's website and services, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing this site.
            </p>
            <h3 class="text-lg font-semibold">Use License</h3>
            <ul class="list-disc pl-5">
              <li>Permission is granted to temporarily access the materials (information or software) on Insurance Express's website for personal, non-commercial transitory viewing only.</li>
              <li>This is the grant of a license, not a transfer of title, and under this license you may not:
                <ul class="list-disc pl-5">
                  <li>Modify or copy the materials</li>
                  <li>Use the materials for any commercial purpose</li>
                  <li>Attempt to decompile or reverse engineer any software contained on Insurance Express's website</li>
                  <li>Remove any copyright or other proprietary notations from the materials</li>
                  <li>Transfer the materials to another person or "mirror" the materials on any other server</li>
                </ul>
              </li>
            </ul>
            <h3 class="text-lg font-semibold">Service Description</h3>
            <p>
              Insurance Express provides an online platform for users to obtain insurance quotes, purchase insurance policies, and manage their insurance accounts. Our services include but are not limited to:
            </p>
            <ul class="list-disc pl-5">
              <li>Insurance quote generation</li>
              <li>Policy management</li>
              <li>Claims processing assistance</li>
              <li>Customer support</li>
            </ul>
            <h3 class="text-lg font-semibold">User Obligations</h3>
            <p>As a user of our services, you agree to:</p>
            <ul class="list-disc pl-5">
              <li>Provide accurate and complete information when using our services</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
              <li>Comply with all applicable laws and regulations</li>
            </ul>
            <h3 class="text-lg font-semibold">Disclaimer</h3>
            <p>
              The materials on Insurance Express's website are provided on an 'as is' basis. Insurance Express makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including, without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.
            </p>
            <h3 class="text-lg font-semibold">Limitations</h3>
            <p>
              In no event shall Insurance Express or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on Insurance Express's website.
            </p>
            <h3 class="text-lg font-semibold">Governing Law</h3>
            <p>
              These terms and conditions are governed by and construed in accordance with the laws of Florida and you irrevocably submit to the exclusive jurisdiction of the courts in that State.
            </p>
            <h3 class="text-lg font-semibold">Changes to Terms</h3>
            <p>
              Insurance Express reserves the right, at its sole discretion, to modify or replace these Terms at any time. What constitutes a material change will be determined at our sole discretion. By continuing to access or use our Service after those revisions become effective, you agree to be bound by the revised terms.
            </p>
            <h3 class="text-lg font-semibold">Contact Information</h3>
            <p>
              If you have any questions about these Terms, please contact us at:<br />
              Email: customerservice@insuranceexpress.com<br />
              Phone: 800-268-9137<br />
              Address: 2005 Vista Parkway Suite 200, West Palm Beach, FL 33411
            </p>
          </div>
        </div>
      </div>
    </transition>
  </footer>
</template>

<style scoped>
/* Modern Footer Styles */
.footer-modern {
  margin-top: 5rem;
}

.footer-main {
  background-color: var(--color-bg-subtle);
  border-top: 1px solid var(--color-border);
  position: relative;
}

.footer-section {
  position: relative;
}

.footer-heading {
  color: var(--color-text-dark);
  font-size: 1.125rem;
  font-weight: var(--font-bold);
  margin-bottom: 1.5rem;
  letter-spacing: -0.025em;
  font-family: var(--font-sans);
}

.footer-links {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer-links li {
  margin-bottom: 0.75rem;
}

.footer-link {
  color: var(--color-text-light);
  font-size: 0.875rem;
  text-decoration: none;
  transition: color 0.2s ease;
  line-height: 1.5;
  font-family: var(--font-sans);
}

.footer-link:hover {
  color: var(--color-primary);
}

.contact-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.contact-icon {
  color: var(--color-primary);
  flex-shrink: 0;
  margin-top: 0.125rem;
  width: 20px;
  height: 20px;
}

.cta-button {
  display: inline-flex;
  align-items: center;
  background-color: var(--color-primary);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 0.375rem;
  font-weight: var(--font-medium);
  text-decoration: none;
  transition: all 0.2s ease;
  font-family: var(--font-sans);
  border: none;
  cursor: pointer;
}

.cta-button:hover {
  background-color: var(--color-primary-dark);
  transform: translateY(-1px);
}

.footer-brand img {
  height: 3rem;
  margin-bottom: 1.5rem;
}

.footer-brand p {
  color: var(--color-text-light);
  font-size: 0.875rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  max-width: 20rem;
  font-family: var(--font-sans);
}

.footer-brand-logo {
  height: 3rem;
  margin-bottom: 1.5rem;
}

.footer-brand-description {
  color: var(--color-text-light);
  font-size: 0.875rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  max-width: 20rem;
  font-family: var(--font-sans);
}

.trust-badge {
  display: flex;
  align-items: center;
  background-color: white;
  border: 1px solid var(--color-border);
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
  color: var(--color-text-dark);
  font-weight: var(--font-medium);
}

.trust-badge svg {
  color: #10B981;
  margin-right: 0.5rem;
}

.footer-bottom {
  background-color: var(--color-text-light);
  border-top: 1px solid var(--color-border-strong);
}

.footer-bottom-link {
  color: #E5E7EB;
  text-decoration: none;
  transition: color 0.2s ease;
  font-family: var(--font-sans);
}

.footer-bottom-link:hover {
  color: white;
}

/* Member Companies Modal Styles */
.member-company-card {
  background: white;
  border: 1px solid var(--color-border);
  border-radius: 0.5rem;
  padding: 1rem;
  text-decoration: none;
  transition: all 0.2s ease;
  display: block;
}

.member-company-card:hover {
  border-color: var(--color-primary);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background-color: white;
  border-radius: 0.75rem;
  width: 100%;
  max-width: 800px;
  position: relative;
  text-align: left;
  max-height: 90vh;
  overflow-y: auto;
  padding: 2rem;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
}

.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background-color: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
}

.modal-close:hover {
  background-color: #f3f4f6;
  color: #374151;
}

/* Modal Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-content,
.modal-leave-active .modal-content {
  transition: transform 0.3s ease;
}

.modal-enter-from .modal-content,
.modal-leave-to .modal-content {
  transform: scale(0.9) translateY(20px);
}

/* Responsive Design */
@media (max-width: 768px) {
  .footer-main {
    padding-top: 3rem;
    padding-bottom: 3rem;
  }
  
  .footer-heading {
    font-size: 1rem;
    margin-bottom: 1rem;
  }
  
  .contact-item {
    flex-direction: row;
    align-items: flex-start;
  }
  
  .cta-button {
    width: 100%;
    justify-content: center;
  }
  
  .modal-content {
    margin: 1rem;
    padding: 1.5rem;
  }
}

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
  .footer-link,
  .cta-button,
  .member-company-card {
    transition: none;
  }
  
  .cta-button:hover,
  .member-company-card:hover {
    transform: none;
  }
}
</style>

<script>
import { Icon } from '@iconify/vue';
import logoPath from '@/assets/images/insurance_express-main-logo.svg';

export default {
  name: 'AppFooter',
  components: {
    Icon
  },
  data() {
    return {
      logoPath,
      showPrivacyPolicy: false,
      showTermsOfService: false,
      showMemberCompanies: false,
      memberCompanies: [
        { name: 'Rocket Flood', link: 'https://rocketflood.com/', description: 'Flood insurance specialists' },
        { name: 'Rocket MGA', link: 'https://rocketmga.com/', description: 'Managing general agency services' },
        { name: 'Str8line Group', link: 'http://www.str8linegroup.com/', description: 'Insurance solutions provider' },
        { name: 'QuoteSlash', link: 'https://quoteslash.com/', description: 'Digital insurance platform' },
        { name: 'Protego VIP', link: 'https://protegovip.com/', description: 'VIP protection services' },
        { name: 'TheLeadExchange', link: 'https://theleadexchange.com/', description: 'Lead generation platform' },
        { name: 'NuBuild', link: 'https://nubuildinsurance.com/', description: 'Construction insurance' },
        { name: 'The IE Pipeline', link: 'https://iepipeline.com/', description: 'Insurance pipeline solutions' },
        { name: 'Graham Newton & McMahon Inc.', link: 'https://gnminsurance.com/', description: 'Professional liability insurance' },
        { name: 'Noble Care', link: 'https://www.noblecareinsurance.com/', description: 'Healthcare insurance solutions' }
      ]
    };
  }
};
</script>
